import { createFileRoute, Link } from "@tanstack/react-router"
import { Plus } from "lucide-react"
import { PageHeader, Panel } from "@/components/layout"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Skeleton } from "@/components/ui/skeleton"
import { StatusDot } from "@/components/ui/status"
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table"
import { useIntegrationApps } from "@/queries/marketplace"

export const Route = createFileRoute("/integration-apps/")({
	component: IntegrationAppsPage,
})

/** Lists every integration app with its status, featured and privilege badges. */
function IntegrationAppsPage() {
	const { apps, isLoading, error } = useIntegrationApps()

	return (
		<div className="flex flex-col gap-6 p-6">
			<PageHeader
				title="Integration apps"
				count={isLoading ? undefined : apps.length}
				description="Third-party platforms that act on CardNexus accounts on their users' behalf"
				actions={
					<Button asChild>
						<Link to="/integration-apps/new">
							<Plus className="h-4 w-4" />
							Mint app
						</Link>
					</Button>
				}
			/>

			{error && (
				<div className="rounded-lg bg-bad-soft px-4 py-3 text-bad">
					Error: {error.message}
				</div>
			)}

			{isLoading ? (
				<div className="flex flex-col gap-1">
					<Skeleton className="h-9 w-full" />
					<Skeleton className="h-9 w-full" />
					<Skeleton className="h-9 w-full" />
				</div>
			) : apps.length === 0 ? (
				<p className="text-muted-foreground">
					No integration apps yet. Mint the first app to hand a platform its
					credential.
				</p>
			) : (
				<Panel>
					<Table>
						<TableHeader>
							<TableRow>
								<TableHead>App</TableHead>
								<TableHead>Slug</TableHead>
								<TableHead className="text-right">Scopes</TableHead>
								<TableHead>Status</TableHead>
								<TableHead>Webhooks</TableHead>
								<TableHead className="text-right">Created</TableHead>
							</TableRow>
						</TableHeader>
						<TableBody>
							{apps.map((app) => (
								<TableRow key={app.id}>
									<TableCell>
										<Link
											to="/integration-apps/$appId"
											params={{ appId: app.id }}
											className="flex items-center gap-2.5 font-medium hover:text-primary-text"
										>
											{app.logoUrl ? (
												<img
													src={app.logoUrl}
													alt=""
													className="size-5 rounded"
												/>
											) : (
												<span className="size-5 rounded bg-muted" />
											)}
											{app.name}
										</Link>
									</TableCell>
									<TableCell className="font-mono text-xs">
										{app.slug}
									</TableCell>
									<TableCell className="text-right tabular-nums">
										{app.scopes.length}
									</TableCell>
									<TableCell>
										<div className="flex items-center gap-3">
											<StatusDot
												tone={app.status === "enabled" ? "ok" : "neutral"}
											>
												{app.status === "enabled" ? "Enabled" : "Disabled"}
											</StatusDot>
											{app.isDevelopment && <Badge variant="info">Dev</Badge>}
											{app.isFeatured && (
												<Badge variant="neutral">Featured</Badge>
											)}
											{app.canCreateManagedAccounts && (
												<Badge variant="neutral">Managed accounts</Badge>
											)}
											{app.canPushTrackingEvents && (
												<Badge variant="neutral">Tracking push</Badge>
											)}
										</div>
									</TableCell>
									<TableCell
										className={
											app.svixAppId ? "text-muted-foreground" : "text-meta"
										}
									>
										{app.svixAppId ? "Provisioned" : "—"}
									</TableCell>
									<TableCell className="text-right text-muted-foreground tabular-nums">
										{new Date(app.createdAt).toLocaleDateString()}
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				</Panel>
			)}
		</div>
	)
}
